import type { ReactNode } from 'react';
import { Text } from '@lmn/design-system';
import styled, { css } from 'styled-components';
import { IconTrain } from '@lmn/lastminute-react-icons/lastminute-icons-solid.js';
import { IconArrowLeftSolid, IconCross, IconPlaneNe } from '@/app/common/icons/libraryIcons';
import { useSearchSummary } from '@/app/components/hotel-detail/SearchSummary';
import { useTranslation } from '@/app/components/translations/Translation';

const IconContainer = styled.div`
  display: flex;
`;

const CloseCrossButton = styled(IconCross).attrs({
  role: 'button',
  tabIndex: 0,
  'aria-label': 'close',
})`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CloseArrowButton = styled(IconArrowLeftSolid).attrs({
  role: 'button',
  tabIndex: 0,
  'aria-label': 'close',
})`
  position: absolute;
  width: 16px;
  height: 16px;
  cursor: pointer;
  left: 24px;
`;

type ChangeTransportHeaderType = {
  closeModal: () => void;
  isTrain: boolean;
};
export function ChangeTransportHeader(props: ChangeTransportHeaderType) {
  const { closeModal, isTrain } = props;
  const { tripDates } = useSearchSummary({
    useRoomDates: false,
    intlDateFormat: { weekday: 'short', day: 'numeric', month: 'short' },
  });
  const { t } = useTranslation();

  return (
    <div
      css={css`
        display: flex;
        padding: 16px 24px;
      `}
    >
      <CloseCrossButton onClick={closeModal} />
      <div
        css={`
          flex-grow: 1;
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
        `}
      >
        <div
          css={`
            display: flex;
          `}
        >
          {isTrain ? (
            <IconContainer>
              <IconTrain
                css={css`
                  width: 24px;
                  height: 24px;
                  background-color: ${(p) => p.theme.commons.colors.gray600};
                  color: ${(p) => p.theme.commons.colors.gray0};
                  border-radius: 6px;
                  padding: 4px;
                  margin-right: 8px;
                `}
              />
            </IconContainer>
          ) : (
            <IconContainer>
              <IconPlaneNe
                css={css`
                  width: 24px;
                  height: 24px;
                  background-color: ${(p) => p.theme.commons.colors.gray600};
                  color: ${(p) => p.theme.commons.colors.gray0};
                  border-radius: 6px;
                  padding: 4px;
                  margin-right: 8px;
                `}
              />
            </IconContainer>
          )}
        </div>
        <div
          css={css`
            font-weight: ${({ theme }) => theme.commons.font.weights.semiBold};
            margin-right: 1ch;
          `}
        >
          {isTrain ? `${t('vacation.trainavailability.title2')}:` : `${t('vacation.flightavailability.title2')}:`}
        </div>
        <div>{`${tripDates?.checkinFormatted} - ${tripDates?.checkoutFormatted}`}</div>
      </div>
    </div>
  );
}

type ChangeTransportHeaderMobileType = {
  closeModal: () => void;
  isTrain: boolean;
  children: ReactNode;
};
export function ChangeTransportHeaderMobile(props: ChangeTransportHeaderMobileType) {
  const { closeModal, isTrain, children } = props;
  const { tripDates } = useSearchSummary({
    useRoomDates: false,
    intlDateFormat: { weekday: 'short', day: 'numeric', month: 'short' },
  });
  const { t } = useTranslation();

  return (
    <div
      css={css`
        position: sticky;
        top: 0;
        left: 0;
        z-index: 1;
        background-color: white;
        box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
      `}
    >
      <div
        css={css`
          display: flex;
          padding: 12px 16px;
          align-items: center;
          justify-content: center;
          position: relative;
        `}
      >
        <CloseArrowButton onClick={closeModal} />
        <div
          css={`
            display: flex;
            flex-direction: column;
            align-items: center;
          `}
        >
          <Text variant="Title4">
            {isTrain ? `${t('vacation.trainavailability.title2')}` : `${t('vacation.flightavailability.title2')}`}
          </Text>
          <Text variant="BodySmall">{`${tripDates?.checkinFormatted} - ${tripDates?.checkoutFormatted}`}</Text>
        </div>
      </div>
      {children}
    </div>
  );
}
